import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import api from '../utils/api';

const SEGMENT_COLORS = {
  cell_a: '#3E68B2',
  cell_b: '#e67e22',
  cell_c: '#27ae60',
  cell_d: '#e74c3c',
  cell_e: '#9370db',
  cell_f: '#16a085',
  cell_g: '#f39c12',
  cell_h: '#d35400',
};

const SEGMENT_LABELS = {
  cell_a: 'CE-01–15',
  cell_b: 'CE-16–30',
  cell_c: 'CE-31–45',
  cell_d: 'CE-46–60',
  cell_e: 'CE-61–75',
  cell_f: 'CE-76–90',
  cell_g: 'CE-91–105',
  cell_h: 'CE-106–120',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  let dateStr = label;
  try { dateStr = format(parseISO(label + '-01'), 'MMM yyyy'); } catch {}
  const visible = payload.filter(p => p.value != null);
  return (
    <div style={{
      background: 'white', border: '1px solid #dce3ec',
      borderRadius: 6, padding: '8px 12px', fontSize: 11,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    }}>
      <div style={{ fontWeight: 700, color: '#142D55', marginBottom: 4 }}>{dateStr}</div>
      {visible.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#6b7a8d', minWidth: 72 }}>{SEGMENT_LABELS[p.dataKey] ?? p.dataKey}</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {Number(p.value).toFixed(3)} V
          </span>
        </div>
      ))}
    </div>
  );
};

export default function MonthlyAvgChart({ projectId }) {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [segments, setSegments]   = useState([]);
  const [hidden, setHidden]       = useState(new Set());
  const [monthFrom, setMonthFrom] = useState('');
  const [monthTo, setMonthTo]     = useState('');

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setHidden(new Set());
    api.get(`/api/projects/${projectId}/monthly-avg`)
      .then(res => {
        const rows = res.data || [];
        setData(rows);
        setSegments(Object.keys(SEGMENT_COLORS).filter(k => rows.some(r => r[k] != null)));
        if (rows.length) {
          setMonthFrom(rows[0].month);
          setMonthTo(rows[rows.length - 1].month);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const filteredData = data.filter(r =>
    (!monthFrom || r.month >= monthFrom) &&
    (!monthTo   || r.month <= monthTo)
  );

  const toggle = (seg, e) => {
    if (e.shiftKey) {
      setHidden(prev => {
        const next = new Set(prev);
        next.has(seg) ? next.delete(seg) : next.add(seg);
        return next;
      });
    } else {
      const allOthersHidden = segments.filter(s => s !== seg).every(s => hidden.has(s));
      setHidden(allOthersHidden ? new Set() : new Set(segments.filter(s => s !== seg)));
    }
  };

  const formatMonth = val => {
    try { return format(parseISO(val + '-01'), "MMM ''yy"); } catch { return val; }
  };

  if (loading) return <div style={{ padding: 16, color: '#6b7a8d', fontSize: 12 }}>Loading…</div>;
  if (!data.length) return (
    <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9aa3af', fontSize: 12 }}>
      No data yet
    </div>
  );

  const allMonths = data.map(r => r.month);

  return (
    <div>
      {/* Period range pickers */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#6b7a8d', fontWeight: 500 }}>Period:</span>
        <input
          type="month"
          value={monthFrom}
          min={allMonths[0]}
          max={monthTo || allMonths[allMonths.length - 1]}
          onChange={e => setMonthFrom(e.target.value)}
          style={{ fontSize: 12, padding: '3px 7px', borderRadius: 5, border: '1px solid #dce3ec', color: '#2c3e50' }}
        />
        <span style={{ fontSize: 11, color: '#9aa3af' }}>→</span>
        <input
          type="month"
          value={monthTo}
          min={monthFrom || allMonths[0]}
          max={allMonths[allMonths.length - 1]}
          onChange={e => setMonthTo(e.target.value)}
          style={{ fontSize: 12, padding: '3px 7px', borderRadius: 5, border: '1px solid #dce3ec', color: '#2c3e50' }}
        />
        {(monthFrom !== allMonths[0] || monthTo !== allMonths[allMonths.length - 1]) && (
          <button
            onClick={() => { setMonthFrom(allMonths[0]); setMonthTo(allMonths[allMonths.length - 1]); }}
            style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 5,
              border: '1px solid #3E68B2', background: '#eef2fb',
              color: '#3E68B2', cursor: 'pointer', fontWeight: 600,
            }}
          >
            All time
          </button>
        )}
        <span style={{ fontSize: 11, color: '#9aa3af', marginLeft: 4 }}>
          {filteredData.length} month{filteredData.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Clickable legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 6px', marginBottom: 10 }}>
        {segments.map(seg => {
          const isHidden = hidden.has(seg);
          return (
            <div
              key={seg}
              onClick={e => toggle(seg, e)}
              title="Click to isolate · Shift+click to toggle"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 9px',
                borderRadius: 12,
                border: `1px solid ${isHidden ? '#dce3ec' : SEGMENT_COLORS[seg]}`,
                background: isHidden ? '#f5f7fa' : `${SEGMENT_COLORS[seg]}18`,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 500,
                color: isHidden ? '#aab0bc' : '#2c3e50',
                userSelect: 'none',
                transition: 'all 0.15s',
              }}
            >
              <span style={{
                width: 10, height: 3, borderRadius: 2, flexShrink: 0,
                background: isHidden ? '#ccc' : SEGMENT_COLORS[seg],
                display: 'inline-block',
              }} />
              {SEGMENT_LABELS[seg]}
            </div>
          );
        })}
        {hidden.size > 0 && (
          <div
            onClick={() => setHidden(new Set())}
            style={{
              padding: '3px 9px', borderRadius: 12,
              border: '1px solid #3E68B2', background: '#eef2fb',
              cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#3E68B2',
              userSelect: 'none',
            }}
          >
            Show all
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={filteredData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
          <XAxis
            dataKey="month"
            tickFormatter={formatMonth}
            tick={{ fontSize: 10, fill: '#6b7a8d' }}
            tickLine={false}
            axisLine={{ stroke: '#dce3ec' }}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#6b7a8d' }}
            tickLine={false}
            axisLine={false}
            width={40}
            tickFormatter={v => v.toFixed(2)}
            domain={['auto', 'auto']}
          />
          <Tooltip content={<CustomTooltip />} />
          {segments.map(seg => (
            <Line
              key={seg}
              type="monotone"
              dataKey={seg}
              stroke={SEGMENT_COLORS[seg]}
              dot={{ r: 3, fill: SEGMENT_COLORS[seg] }}
              strokeWidth={2}
              hide={hidden.has(seg)}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
